<template>
	<v-row no-gutters class="pa-2 overflow-auto" style="height: 100%; width: 100%">
		<!-- Time tracking of the user in this page -->
		<UserTimeTracker page-key="ccmMedicationEvent" />

		<v-col cols="12" class="pa-1" style="height: 100%">
			<v-card style="height: 100%; min-height: 300px" :loading="loadingStatus">
				<v-card-title class="py-2 py-lg-4">
					<v-row no-gutters>
						<v-col class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
							{{ $t('medications') }}
						</v-col>
						<v-spacer />
						<v-col class="text-right">
							<ActionButton
								icon="mdi-file-delimited"
								tooltip="download-csv"
								:maxSize="25"
								:minSize="20"
								@clicked="downloadCSV()"
								:busy="downloading" />

							<v-icon
								color="primary"
								:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
								@click="synchronizeAddDrawers('ccmMedicationEventAdd')">
								mdi-plus-circle
							</v-icon>
						</v-col>
					</v-row>
				</v-card-title>
				<v-card-text
					class="overflow-auto"
					:style="$vuetify.breakpoint.lgAndUp ? 'height: calc(100% - 64px)' : 'height: calc(100% - 48px)'">
					<v-data-table
						:items="medicationEvents"
						:headers="headers"
						:items-per-page="-1"
						:key="key"
						style="overflow-y: auto"
						:height="pageHeight($vuetify.breakpoint.mdAndDown ? 93 : 77)"
						fixed-header
						dense
						sort-by="eventDate"
						sort-desc
						class="data-table-outline overflow-y-auto"
						:footer-props="{ 'items-per-page-text': '' }">
						<template v-slot:item="{ item, index }">
							<tr :class="getRowsForDataTable(index)" @click.stop="openViewDialog(item)">
								<td class="text-left px-1 truncate-text" style="max-width: 14vw">
									{{ item.medicationName }}
								</td>
								<td class="text-left px-1 truncate-text" style="max-width: 14vw">
									{{ item.dosage }}
								</td>
								<td class="text-left px-1">
									{{ $t(item.isMedicationHasEnded ? 'finished' : 'on-going') }}
								</td>
								<td class="text-left px-1">
									{{ item.performedBy }}
								</td>
								<td class="text-left px-1">
									{{ formatTimestamp(item.eventDate) }}
								</td>
								<td class="text-left px-1">
									{{ formatDuration(item.timeSpentSeconds) }}
								</td>
								<td class="px-1">
									<ActionButton
										v-if="item.ccmRecordId === selectedCCMRecord.id && item.isMedicationHasEnded"
										icon="mdi-delete"
										tooltip="delete"
										:maxSize="25"
										:minSize="20"
										@clicked="
											deleteId = item.id;
											showDeleteDialog = true;
										"
										:busy="deletingIds.includes(item.id)" />

									<v-tooltip left v-if="item.ccmRecordId === selectedCCMRecord.id">
										<template v-slot:activator="{ on }">
											<v-icon
												class="mt-1"
												color="primary"
												:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
												v-on="on"
												@click.stop="selectMedicationEventToEdit(item)">
												mdi-pencil
											</v-icon>
										</template>
										<span>{{ $t('edit') }}</span>
									</v-tooltip>
								</td>
							</tr>
						</template>
						<template v-slot:[`footer.prepend`]>
							<v-row dense class="ma-0">
								<v-col class="text-left">
									<p class="ma-0">
										<v-tooltip top>
											<template v-slot:activator="{ on }">
												<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
											</template>
											{{ $t('need-to-select-the-correct-record') }}
										</v-tooltip>
									</p>
								</v-col>
							</v-row>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-col>
		<CCMMedicationEventViewDialog
			:dialog.sync="showViewDialog"
			v-if="showViewDialog && selectedMedicationEvent"
			:selectedMedicationEvent="selectedMedicationEvent"
			@closeDialog="showViewDialog = false" />

		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deletingIds.push(deleteId);
				deleteMedicationEvent(deleteId);
				deleteId = -1;
			" />
	</v-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	getRowsForDataTable,
	formatTimestamp,
	formatDuration,
	downloadCSVWithKeys,
	getFullName,
} from '@/utils/utilities';
import CCMMedicationEventViewDialog from '../../../dialogs/CCMMedicationEventViewDialog.vue';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';
import UserTimeTracker from '@/views/Client/components/UserTimeTracker.vue';
import ActionButton from '../ActionButton.vue';

export default {
	name: 'CCMMedicationEventAdd',
	components: {
		CCMMedicationEventViewDialog,
		DeleteDialog,
		UserTimeTracker,
		ActionButton,
	},

	data () {
		return {
			showViewDialog: false,
			loadingStatus: true,
			deleteId: -1,
			deletingIds: [],
			showDeleteDialog: false,
			key: 0,
			downloading: false,
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			medicationEvents: (state) => state.ccmMedicationEvents.ccmMedicationEventByPatientId,
			selectedCCMRecord: (state) => state.ccmRecords.ccmRecord,
		}),

		...mapGetters({
			pageHeight: 'pageHeight',
		}),

		headers () {
			return [
				{
					text: this.$t('medication-name'),
					sortable: false,
					value: 'medicationName',
					class: 'color_header px-1 text-left',
				},
				{
					text: this.$t('dosage'),
					sortable: false,
					value: 'dosage',
					class: 'color_header px-1 text-left',
				},
				{
					text: this.$t('course-status'),
					sortable: false,
					value: 'isMedicationHasEnded',
					class: 'color_header px-1 text-left',
				},
				{
					text: this.$t('performed-by'),
					sortable: false,
					value: 'performedBy',
					class: 'color_header px-1 text-left',
				},
				{
					text: this.$t('event-date'),
					sortable: false,
					value: 'eventDate',
					class: 'color_header px-1 text-left',
				},
				{
					text: this.$t('time-spent-header'),
					sortable: false,
					value: 'timeSpentSeconds',
					class: 'color_header px-1 text-left',
				},
				{
					text: this.$t('actions'),
					sortable: false,
					value: '',
					align: 'center',
					class: 'color_header px-1 text-left',
				},
			];
		},
	},

	methods: {
		...mapActions({
			getMedicationEvent: 'ccmMedicationEvents/getCCMMedicationEventByPatientId',
			getMedicationEventById: 'ccmMedicationEvents/getCCMMedicationEvent',
			deleteMedicationEventById: 'ccmMedicationEvents/deleteCCMMedicationEvent',
			storeCCMRecordsByPatientId: 'ccmRecords/storeMinimalCCMRecordsByPatientId',
		}),

		getRowsForDataTable,
		formatTimestamp,
		formatDuration,
		downloadCSVWithKeys,
		getFullName,

		synchronizeAddDrawers (drawer) {
			const parameters = {
				detailsDrawer: true,
				page: drawer,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		synchronizeEditDrawers (drawer) {
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'ccmDetails',
			});
		},
		async selectMedicationEventToEdit (item) {
			this.getMedicationEventById(item.id);
			this.synchronizeEditDrawers('ccmMedicationEventEdit', item.id);
		},
		async deleteMedicationEvent (id) {
			await this.deleteMedicationEventById(id);
			await this.storeCCMRecordsByPatientId(this.detailedUser.userData.id);
			this.deletingIds = this.deletingIds.filter((item) => item !== id);
		},
		openViewDialog (medicationEvent) {
			this.selectedMedicationEvent = medicationEvent;
			this.showViewDialog = true;
		},
		async downloadCSV () {
			this.downloading = true;
			await downloadCSVWithKeys(
				this.medicationEventsForCSV,
				'Medications_for_' + getFullName(this.detailedUser.userData),
				{
					ids: ['ccmRecordId'],
					data: [
						'medicationName',
						'genericName',
						'courseStatus',
						'dosage',
						'frequency',
						'route',
						'durationDays',
						'manufacturerName',
						'additionalinfo',
						'performedBy',
						'performedByRole',
					],
					timestamps: ['eventDate'],
					timeSpent: ['timeSpentSeconds'],
				},
			);
			this.downloading = false;
		},
	},

	async mounted () {
		if (!this.loadingStatus) {
			this.loadingStatus = false;
		}
		await this.getMedicationEvent(this.detailedUser.userData.id);
		this.loadingStatus = false;
	},

	watch: {
		selectedCCMRecord () {
			this.key = !this.key;
		},
		medicationEvents () {
			this.medicationEventsForCSV = this.medicationEvents.map((item) => ({
				...item,
				courseStatus: item.isMedicationHasEnded,
			}));
		},
	},
};
</script>
